import { DatePipe } from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  CUSTOM_ELEMENTS_SCHEMA,
  ElementRef,
  EventEmitter,
  Output,
  computed,
  input,
  signal,
  viewChild,
} from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { IonicModule } from '@ionic/angular';
import { TranslateModule } from '@ngx-translate/core';
import { interval } from 'rxjs';
import { map, startWith } from 'rxjs/operators';
import { BOOKING_SLOT_MINUTES, ResourceBusySchedule } from 'src/app/interfaces/interfaces';

interface WeekSlide {
  key: string;
  start: Date;
  days: Date[];
}

interface DisplayBusySlot {
  id: string;
  dayIndex: number;
  start: Date;
  end: Date;
  top: string;
  gridOffset: string;
  height: string;
  isPast: boolean;
  showTime: boolean;
}

interface RawBusySlot {
  start?: string;
  end?: string;
  Start?: string;
  End?: string;
}

interface SlotCell {
  start: Date;
  end: Date;
  available: boolean;
  top: string;
  isBetweenBusySlots: boolean;
  maxEnd: Date;
  isPast: boolean;
}

@Component({
  selector: 'app-resource-week-calendar',
  templateUrl: './resource-week-calendar.component.html',
  styleUrls: ['./resource-week-calendar.component.scss'],
  imports: [DatePipe, IonicModule, TranslateModule],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
})
export class ResourceWeekCalendarComponent implements AfterViewInit {
  readonly busySchedule = input<ResourceBusySchedule>({});
  readonly loading = input<boolean>(false);
  readonly locale = input<string>('en-GB');
  readonly workingDays = input<number[] | null>(null);

  @Output() weekChanged = new EventEmitter<Date>();
  @Output() slotSelected = new EventEmitter<{ start: Date; end: Date; isBetweenBusySlots: boolean; maxEnd: Date }>();

  protected readonly initialSlideIndex = 26;
  protected readonly weekSlides = this.generateWeekSlides(
    this.initialSlideIndex,
  );
  protected readonly activeSlideIndex = signal(this.initialSlideIndex);
  private readonly scroll = viewChild<ElementRef<HTMLElement>>('scrollView');
  private readonly weekSwiper =
    viewChild<ElementRef<HTMLElement & { swiper?: any }>>('weekSwiper');
  private hasInitializedSwiper = false;

  private readonly time$ = interval(1000).pipe(
    startWith(0),
    map(() => new Date()),
    takeUntilDestroyed(),
  );
  protected readonly now = toSignal<Date>(this.time$);

  private readonly slotTime$ = interval(60000).pipe(
    startWith(0),
    map(() => new Date()),
    takeUntilDestroyed(),
  );
  protected readonly slotNow = toSignal<Date>(this.slotTime$);

  protected readonly activeWeek = computed(() => {
    return (
      this.weekSlides[this.activeSlideIndex()] ||
      this.weekSlides[this.initialSlideIndex]
    );
  });

  protected readonly activeWeekLabel = computed(() => {
    const week = this.activeWeek();
    const end = this.addDays(week.start, 6);
    const formatter = new Intl.DateTimeFormat(this.locale(), {
      day: 'numeric',
      month: 'short',
    });

    return `${formatter.format(week.start)} - ${formatter.format(end)}`;
  });

  protected readonly activeWeekEvents = computed(() => {
    return this.displayEventsForWeek(this.activeWeek());
  });

  protected readonly slotCells = computed(() => {
    return this.generateSlotCells(this.activeWeek());
  });

  protected readonly currentTop = computed(() => {
    const now = this.now() || new Date();
    const startOfDay = new Date(now);
    startOfDay.setHours(0, 0, 0, 0);
    return this.msToRem(now.getTime() - startOfDay.getTime());
  });

  protected readonly hours = Array.from({ length: 24 }, (_, hour) => {
    const date = new Date();
    date.setHours(hour, 0, 0, 0);
    return date;
  });

  ngAfterViewInit() {
    this.syncSwiperToInitialSlide();
    this.scrollToCurrentTime();
  }

  protected onSlotTap(cell: SlotCell) {
    if (!cell.available) {
      return;
    }
    this.slotSelected.emit({ start: cell.start, end: cell.end, isBetweenBusySlots: cell.isBetweenBusySlots, maxEnd: cell.maxEnd });
  }

  protected onWeekSlideChange(event: Event | CustomEvent<any>) {
    const customEvent = event as CustomEvent<any>;
    const swiper = (event.target as any)?.swiper || customEvent.detail?.[0];
    const activeIndex = swiper?.activeIndex;

    if (!Number.isInteger(activeIndex) || !this.weekSlides[activeIndex]) {
      return;
    }

    if (!this.hasInitializedSwiper && activeIndex !== this.initialSlideIndex) {
      return;
    }

    if (activeIndex === this.activeSlideIndex()) {
      return;
    }

    this.activeSlideIndex.set(activeIndex);

    if (this.hasInitializedSwiper) {
      this.weekChanged.emit(new Date(this.weekSlides[activeIndex].start));
    }
  }

  private displayEventsForWeek(week: WeekSlide): DisplayBusySlot[] {
    const schedule = this.busySchedule() || {};
    const now = this.slotNow() || new Date();
    const events: DisplayBusySlot[] = [];

    Object.entries(schedule).forEach(([dateKey, slots]) => {
      if (!Array.isArray(slots)) {
        return;
      }

      slots.forEach((slot, index) => {
        const rawSlot = slot as RawBusySlot;
        const start = this.parseScheduleDate(rawSlot.start || rawSlot.Start);
        const end = this.parseScheduleDate(rawSlot.end || rawSlot.End);

        if (!start || !end) {
          return;
        }

        const slotDateKey = this.isDateKey(dateKey)
          ? dateKey
          : this.dateKey(start);
        const dayIndex = week.days.findIndex((day) => {
          return this.dateKey(day) === slotDateKey;
        });

        if (dayIndex === -1) {
          return;
        }

        const dayStart = new Date(week.days[dayIndex]);
        const dayEnd = this.addDays(dayStart, 1);
        const displayStart = start < dayStart ? dayStart : start;
        const displayEnd = end > dayEnd ? dayEnd : end;

        if (displayEnd <= dayStart || displayStart >= dayEnd) {
          return;
        }

        const duration = Math.max(
          displayEnd.getTime() - displayStart.getTime(),
          5 * 60 * 1000,
        );
        const topMs = displayStart.getTime() - dayStart.getTime();

        events.push({
          id: `${dateKey}-${index}-${start.toISOString()}`,
          dayIndex,
          start,
          end,
          top: this.msToRem(topMs),
          gridOffset: this.msToRem(-topMs),
          height: this.msToRem(duration),
          isPast: end < now,
          showTime: duration >= 25 * 60 * 1000,
        });
      });
    });

    return events.sort((a, b) => a.start.getTime() - b.start.getTime());
  }

  private generateSlotCells(week: WeekSlide): Map<number, SlotCell[]> {
    const schedule = this.busySchedule() || {};
    const now = this.slotNow() || new Date();
    const cellsMap = new Map<number, SlotCell[]>();
    const slotsPerDay = (24 * 60) / BOOKING_SLOT_MINUTES;

    week.days.forEach((day, dayIndex) => {
      const dayStart = new Date(day);
      dayStart.setHours(0, 0, 0, 0);
      const cells: SlotCell[] = [];
      const dayBusySlots = schedule[this.dateKey(day)] || [];

      const parsedDayBusySlots = dayBusySlots
        .map((slot: RawBusySlot) => ({
          start: this.parseScheduleDate(slot.start || slot.Start),
          end: this.parseScheduleDate(slot.end || slot.End),
        }))
        .filter((slot) => !!slot.start && !!slot.end) as { start: Date; end: Date }[];

      for (let i = 0; i < slotsPerDay; i++) {
        const cellStart = new Date(dayStart.getTime() + i * BOOKING_SLOT_MINUTES * 60 * 1000);
        const cellEnd = new Date(cellStart.getTime() + BOOKING_SLOT_MINUTES * 60 * 1000);
        const topMs = cellStart.getTime() - dayStart.getTime();

        const isPast = cellStart < now;
        const isBusy = parsedDayBusySlots.some((slot) => {
          return slot.start < cellEnd && slot.end > cellStart;
        });

        const hasBusyBefore = parsedDayBusySlots.some((slot) => {
          return slot.end <= cellStart;
        });

        const hasBusyAfter = parsedDayBusySlots.some((slot) => {
          return slot.start >= cellEnd;
        });

        const isBetweenBusySlots = hasBusyBefore && hasBusyAfter;

        const nextBusyStart = parsedDayBusySlots
          .map((slot) => slot.start)
          .filter((start) => start >= cellEnd)
          .sort((a, b) => a.getTime() - b.getTime())[0];
        const maxEnd = nextBusyStart || new Date(dayStart.getTime() + 24 * 60 * 60 * 1000);

        cells.push({
          start: cellStart,
          end: cellEnd,
          available: !isPast && !isBusy,
          top: this.msToRem(topMs),
          isBetweenBusySlots,
          maxEnd,
          isPast,
        });
      }

      cellsMap.set(dayIndex, cells);
    });

    return cellsMap;
  }

  protected isToday(day: Date) {
    return this.isSameDate(day, new Date());
  }

  protected isPastDay(day: Date) {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return day < today;
  }

  protected isEmphasizedDay(day: Date) {
    return this.isToday(day) || this.isSameDate(day, this.getNextWorkday());
  }

  protected isOffHoursDay(day: Date) {
    const workingDays = this.workingDays();
    const dayOfWeek = day.getDay();

    if (Array.isArray(workingDays) && workingDays.length > 0) {
      return !workingDays.includes(dayOfWeek);
    }

    return dayOfWeek === 0 || dayOfWeek === 6;
  }

  protected isWeekWithToday(week: WeekSlide) {
    return week.days.some((day) => this.isToday(day));
  }

  private syncSwiperToInitialSlide(retries = 10) {
    const swiper = this.weekSwiper()?.nativeElement.swiper;

    if (!swiper) {
      if (retries > 0) {
        setTimeout(() => this.syncSwiperToInitialSlide(retries - 1), 50);
      }
      return;
    }

    if (swiper.activeIndex !== this.initialSlideIndex) {
      swiper.slideTo(this.initialSlideIndex, 0, false);
    }

    this.activeSlideIndex.set(this.initialSlideIndex);
    this.hasInitializedSwiper = true;
  }

  private scrollToCurrentTime(retries = 10) {
    const el = this.scroll()?.nativeElement;

    if (!el || el.clientHeight === 0 || el.scrollHeight === 0) {
      if (retries > 0) {
        setTimeout(() => this.scrollToCurrentTime(retries - 1), 50);
      }
      return;
    }

    const now = new Date();
    const startOfDay = new Date(now);
    startOfDay.setHours(0, 0, 0, 0);
    const topPosRem = parseFloat(
      this.msToRem(now.getTime() - startOfDay.getTime()),
    );

    const remSize =
      parseFloat(getComputedStyle(document.documentElement).fontSize) || 16;
    const currentTimeTop = topPosRem * remSize;
    const maxScroll = Math.max(
      el.scrollHeight - el.clientHeight,
      0,
    );
    const scrollToPos = Math.min(
      Math.max(currentTimeTop - el.clientHeight / 2, 0),
      maxScroll,
    );

    el.scrollTo({
      top: scrollToPos,
    });
  }

  private generateWeekSlides(weeksBeforeAndAfter: number): WeekSlide[] {
    const currentWeekStart = this.getWeekStart(new Date());
    const slides: WeekSlide[] = [];

    for (
      let index = -weeksBeforeAndAfter;
      index <= weeksBeforeAndAfter;
      index++
    ) {
      const weekStart = this.addDays(currentWeekStart, index * 7);
      slides.push({
        key: this.dateKey(weekStart),
        start: weekStart,
        days: Array.from({ length: 7 }, (_, dayIndex) =>
          this.addDays(weekStart, dayIndex),
        ),
      });
    }

    return slides;
  }

  private getWeekStart(date: Date) {
    const weekStart = new Date(date);
    const dayOfWeek = weekStart.getDay();
    const daysFromMonday = dayOfWeek === 0 ? 6 : dayOfWeek - 1;
    weekStart.setDate(weekStart.getDate() - daysFromMonday);
    weekStart.setHours(0, 0, 0, 0);
    return weekStart;
  }

  private getNextWorkday() {
    const nextWorkday = new Date();
    nextWorkday.setHours(0, 0, 0, 0);

    do {
      nextWorkday.setDate(nextWorkday.getDate() + 1);
    } while (nextWorkday.getDay() === 0 || nextWorkday.getDay() === 6);

    return nextWorkday;
  }

  private isSameDate(left: Date, right: Date) {
    return this.dateKey(left) === this.dateKey(right);
  }

  private addDays(date: Date, days: number) {
    const nextDate = new Date(date);
    nextDate.setDate(nextDate.getDate() + days);
    nextDate.setHours(0, 0, 0, 0);
    return nextDate;
  }

  private dateKey(date: Date) {
    const year = date.getFullYear();
    const month = `${date.getMonth() + 1}`.padStart(2, '0');
    const day = `${date.getDate()}`.padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  private isDateKey(value: string) {
    return /^\d{4}-\d{2}-\d{2}$/.test(value);
  }

  private parseScheduleDate(value: string | undefined) {
    if (!value) {
      return null;
    }

    const localDateTime = value.match(
      /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})(?::(\d{2})(?:\.(\d{1,3}))?)?$/,
    );

    if (localDateTime) {
      const [, year, month, day, hour, minute, second = '0', ms = '0'] =
        localDateTime;
      return new Date(
        Date.UTC(
          Number(year),
          Number(month) - 1,
          Number(day),
          Number(hour),
          Number(minute),
          Number(second),
          Number(ms.padEnd(3, '0')),
        ),
      );
    }

    const parsedDate = new Date(value);
    return Number.isNaN(parsedDate.getTime()) ? null : parsedDate;
  }

  private msToRem(timeMs: number): string {
    return timeMs / (10 * 60 * 1000) + 'rem';
  }
}
